import { useMemo, useEffect, useRef } from 'react'
import {
	useIsMobileSm,
	useIsMobileLg,
} from '../../../shared/hooks/useBreakpoint'
import { parseServerInstant } from '../../../shared/parseServerInstant'

const toInstant = ts => {
	const d = parseServerInstant(ts)
	return d && !Number.isNaN(d.getTime()) ? d : null
}

const getLastActivityLabel = iso => {
	if (!iso) return ''

	const date = toInstant(iso)
	if (!date) return ''

	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffMinutes = Math.floor(diffMs / (60 * 1000))

	const isSameDay =
		now.getFullYear() === date.getFullYear() &&
		now.getMonth() === date.getMonth() &&
		now.getDate() === date.getDate()

	if (diffMinutes < 60) {
		const minutes = Math.max(diffMinutes, 1)
		return `Online ${minutes} mins ago`
	}

	if (isSameDay) {
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
		})
	}

	const isSameYear = now.getFullYear() === date.getFullYear()
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: isSameYear ? undefined : 'numeric',
	})
}

const formatMessageDate = timestamp => {
	if (!timestamp) return ''

	const date = toInstant(timestamp)
	if (!date) return ''

	const now = new Date()
	const isSameDay =
		now.getFullYear() === date.getFullYear() &&
		now.getMonth() === date.getMonth() &&
		now.getDate() === date.getDate()

	if (isSameDay) {
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})
	}

	const isSameYear = now.getFullYear() === date.getFullYear()
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: isSameYear ? undefined : 'numeric',
	})
}

const SIXTY_MIN_MS = 60 * 60 * 1000

const isCurrentMonth = date => {
	const now = new Date()
	return (
		date.getFullYear() === now.getFullYear() &&
		date.getMonth() === now.getMonth()
	)
}

/** Format for new calendar day: "25 Feb, 12:45 PM" */
const formatDateSeparatorNewDay = date => {
	const day = date.getDate()
	const month = date.toLocaleDateString('en-US', { month: 'short' })
	const time = date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
	return `${day} ${month}, ${time}`
}

/** Format for same day, gap > 60 min: "3:18 PM" */
const formatDateSeparatorTimeOnly = date => {
	return date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

/** Format for past months/years: "25/02/25, 12:45 PM" */
const formatDateSeparatorPast = date => {
	const d = date.getDate()
	const m = date.getMonth() + 1
	const y = date.getFullYear()
	const pad = n => String(n).padStart(2, '0')
	const yy = String(y).slice(-2)
	const time = date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
	return `${pad(d)}/${pad(m)}/${yy}, ${time}`
}

const getDateSeparatorLabel = (msg, prevMsg, showReason) => {
	const date = toInstant(msg.timestamp)
	if (!date) return ''

	if (showReason === 'first') {
		return isCurrentMonth(date)
			? formatDateSeparatorNewDay(date)
			: formatDateSeparatorPast(date)
	}
	if (showReason === 'newDay') {
		return isCurrentMonth(date)
			? formatDateSeparatorNewDay(date)
			: formatDateSeparatorPast(date)
	}
	if (showReason === 'sameDayGap') {
		return formatDateSeparatorTimeOnly(date)
	}
	return ''
}

/** Returns 'first' | 'newDay' | 'sameDayGap' | null. null = do not show separator. */
const getDateSeparatorReason = (currentMsg, prevMsg) => {
	const currentDate = toInstant(currentMsg.timestamp)
	if (!currentDate) return null

	if (!prevMsg) return 'first'

	const prevDate = toInstant(prevMsg.timestamp)
	if (!prevDate) return 'first'

	const currentDay = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth(),
		currentDate.getDate(),
	)
	const prevDay = new Date(
		prevDate.getFullYear(),
		prevDate.getMonth(),
		prevDate.getDate(),
	)

	if (currentDay.getTime() !== prevDay.getTime()) return 'newDay'

	const gapMs = currentDate.getTime() - prevDate.getTime()
	if (gapMs > SIXTY_MIN_MS) return 'sameDayGap'

	return null
}

const formatSeenTime = timestamp => {
	if (!timestamp) return ''
	const date = toInstant(timestamp)
	if (!date) return ''
	return date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

export const useChatMessage = ({ selectedUser, messages, onMarkAsRead }) => {
	const isMobileSm = useIsMobileSm()
	const isMobileLg = useIsMobileLg()
	const messagesEndRef = useRef(null)
	const seenInSessionRef = useRef(new Set())
	const prevMessagesRef = useRef([])
	const initialUnreadIndexRef = useRef(-1)
	const initializedRef = useRef(false)
	const userIdRef = useRef(null)

	const lastActivityLabel = useMemo(() => {
		if (!selectedUser?.lastActiveAt) return ''
		return getLastActivityLabel(selectedUser.lastActiveAt)
	}, [selectedUser?.lastActiveAt])

	const isOnline = useMemo(() => {
		if (!selectedUser?.lastActiveAt) return false
		const date = toInstant(selectedUser.lastActiveAt)
		if (!date) return false
		const diffMs = Date.now() - date.getTime()
		return diffMs < 60 * 60 * 1000
	}, [selectedUser?.lastActiveAt])

	const avatarSize = useMemo(() => {
		return isMobileLg ? 25 : 30
	}, [isMobileLg])

	// Reset per selected user
	useEffect(() => {
		const id =
			selectedUser?.studentId || selectedUser?.id
				? String(selectedUser.studentId || selectedUser.id)
				: null
		if (userIdRef.current !== id) {
			userIdRef.current = id
			initialUnreadIndexRef.current = -1
			initializedRef.current = false
			seenInSessionRef.current = new Set()
			prevMessagesRef.current = []
		}
	}, [selectedUser])

	// Track which outgoing messages became read during this session
	useEffect(() => {
		const prevById = new Map(
			(prevMessagesRef.current || []).map(m => [m.id, m]),
		)
		const nextSeen = new Set(seenInSessionRef.current)

		;(messages || []).forEach(m => {
			const prev = prevById.get(m.id)
			if (!prev) return
			if (m.from === 'admin' && m.isRead && !prev.isRead && !m.sendError) {
				nextSeen.add(m.id)
			}
		})

		seenInSessionRef.current = nextSeen
		prevMessagesRef.current = messages || []
	}, [messages])

	const processedMessages = useMemo(() => {
		if (!messages || messages.length === 0) return []

		let firstUnreadIndexCurrent = -1

		messages.forEach((msg, idx) => {
			if (
				!msg.isRead &&
				msg.from === 'user' &&
				firstUnreadIndexCurrent === -1
			) {
				firstUnreadIndexCurrent = idx
			}
		})

		// On first load for this user, remember where unread block starts (if any)
		if (!initializedRef.current) {
			initialUnreadIndexRef.current = firstUnreadIndexCurrent
			initializedRef.current = true
		}

		const initialUnreadIndex = initialUnreadIndexRef.current

		const lastIndex = messages.length - 1
		const lastMsg = lastIndex >= 0 ? messages[lastIndex] : null

		let lastOutgoingIndex = -1
		messages.forEach((m, idx) => {
			if (m.from === 'admin') lastOutgoingIndex = idx
		})

		// Only show Seen under last outgoing message (if it's also the last message)
		const statusTargetIndex =
			lastOutgoingIndex === lastIndex && lastMsg?.from === 'admin'
				? lastOutgoingIndex
				: -1

		// Decide where to show New Messages separator
		let newSeparatorIndex =
			typeof initialUnreadIndex === 'number' ? initialUnreadIndex : -1

		// Hide if no unread on initial load
		if (newSeparatorIndex == null || newSeparatorIndex < 0) {
			newSeparatorIndex = -1
		}

		// Hide once counselor has replied (any admin message after first unread)
		if (newSeparatorIndex >= 0) {
			const hasReplyAfter =
				messages.findIndex(
					(m, idx) => idx > newSeparatorIndex && m.from === 'admin',
				) !== -1
			if (hasReplyAfter) {
				newSeparatorIndex = -1
			}
		}

		return messages.map((msg, index) => {
			const prevMsg = index > 0 ? messages[index - 1] : null
			const separatorReason = getDateSeparatorReason(msg, prevMsg)
			const showDateSeparator = separatorReason !== null

			const dateSeparatorLabel = separatorReason
				? getDateSeparatorLabel(msg, prevMsg, separatorReason)
				: ''

			const dateLabel = formatMessageDate(msg.timestamp)

			let showStatusRow = false
			let seenLabel = ''
			if (
				index === statusTargetIndex &&
				msg.from === 'admin' &&
				msg.isRead &&
				!msg.sendError
			) {
				showStatusRow = true
				const seenInSession = seenInSessionRef.current.has(msg.id)
				if (seenInSession) {
					seenLabel = 'Seen'
				} else {
					const timeStr = formatSeenTime(msg.timestamp)
					seenLabel = timeStr ? `Seen, ${timeStr}` : 'Seen'
				}
			}

			return {
				...msg,
				showDateSeparator,
				dateSeparatorLabel,
				dateLabel,
				isFirstUnread: false,
				showNewSeparator: index === newSeparatorIndex,
				showStatusRow,
				seenLabel,
			}
		})
	}, [messages])

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [processedMessages])

	useEffect(() => {
		if (!selectedUser || !messages || messages.length === 0) return

		const unreadMessages = messages.filter(
			msg => !msg.isRead && msg.from === 'user',
		)
		if (unreadMessages.length > 0 && onMarkAsRead) {
			const messageIds = unreadMessages.map(msg => msg.id)
			onMarkAsRead(messageIds)
		}
	}, [selectedUser, messages, onMarkAsRead])

	const headerAvatarSize = avatarSize
	const messageAvatarSize = 38
	const composerAvatarSize = avatarSize

	return {
		isMobileSm,
		isMobileLg,
		lastActivityLabel,
		isOnline,
		headerAvatarSize,
		messageAvatarSize,
		composerAvatarSize,
		processedMessages,
		messagesEndRef,
	}
}
